import { store } from '../index';
import { createError, updateError } from '../slices/crashlytics/crashlyticsSlice';
import { CreateCrashlyticsError } from '../helpers/api_method';

export interface ErrorData {
  id: string;
  timestamp: Date;
  type: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  message: string;
  stack: string;
  url: string;
  userAgent: string;
  userId: string;
  userEmail: string;
  deviceInfo: {
    platform: string;
    browser: string;
    version: string;
    screen: string;
  };
  sessionInfo: {
    sessionId: string;
    pageLoadTime: string;
    memoryUsage: string;
  };
  apiDetails?: {
    endpoint: string;
    method: string;
    statusCode: string;
    requestData: string;
    responseData: string;
  };
  resolved: boolean;
  resolvedBy: string;
  resolvedAt?: Date;
  notes: string;
}

class CrashlyticsService {
  private static instance: CrashlyticsService;
  private errorQueue: ErrorData[] = [];
  private maxQueueSize = 100;
  private isProcessing = false;
  // Keep a short-term cache of recent errors to avoid spamming identical ones
  private recentErrorTimestamps: Map<string, number> = new Map();
  private dedupeWindowMs = 5000; // do not capture identical errors within 5s

  private constructor() {
    // Initialize the service
    this.setupGlobalErrorHandlers();
  }

  public static getInstance(): CrashlyticsService {
    if (!CrashlyticsService.instance) {
      CrashlyticsService.instance = new CrashlyticsService();
    }
    return CrashlyticsService.instance;
  }

  private setupGlobalErrorHandlers(): void {
    // Get current user info helper function
    const getCurrentUserInfo = () => {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          return {
            id: user.id || user.user_id,
            email: user.email
          };
        }
        return { id: null, email: null };
      } catch (error) {
        console.error('Error parsing user data:', error);
        return { id: null, email: null };
      }
    };

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      if (!this.shouldCaptureGlobalEvent(event?.reason?.message)) return;
      const userInfo = getCurrentUserInfo();
      this.captureError({
        type: 'UNHANDLED_PROMISE_REJECTION',
        severity: 'HIGH',
        message: event.reason?.message || 'Unhandled Promise Rejection',
        stack: event.reason?.stack || '',
        url: window.location.href,
        userAgent: navigator.userAgent,
        userId: userInfo.id,
        userEmail: userInfo.email,
        deviceInfo: this.getDeviceInfo(),
        sessionInfo: this.getSessionInfo(),
        resolved: false,
        resolvedBy: '',
        notes: '',
      });
    });

    // Handle JavaScript errors
    window.addEventListener('error', (event) => {
      if (!this.shouldCaptureGlobalEvent(event?.message)) return;
      const userInfo = getCurrentUserInfo();
      this.captureError({
        type: 'JAVASCRIPT_ERROR',
        severity: this.determineErrorSeverity(event.error),
        message: event.message || 'JavaScript Error',
        stack: event.error?.stack || '',
        url: event.filename || window.location.href,
        userAgent: navigator.userAgent,
        userId: userInfo.id,
        userEmail: userInfo.email,
        deviceInfo: this.getDeviceInfo(),
        sessionInfo: this.getSessionInfo(),
        resolved: false,
        resolvedBy: '',
        notes: '',
      });
    });
  }

  private determineErrorSeverity(error: Error | null): 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' {
    if (!error) return 'MEDIUM';
    
    const message = error.message.toLowerCase();
    const stack = error.stack?.toLowerCase() || '';
    
    // Critical errors
    if (message.includes('out of memory') || message.includes('memory leak')) {
      return 'CRITICAL';
    }
    
    // High severity errors
    if (message.includes('network') || message.includes('fetch') || message.includes('api')) {
      return 'HIGH';
    }
    
    // Medium severity for most errors
    return 'MEDIUM';
  }

  private getDeviceInfo() {
    return {
      platform: navigator.platform,
      browser: this.getBrowserInfo(),
      version: navigator.appVersion,
      screen: `${window.screen.width}x${window.screen.height}`,
    };
  }

  private getBrowserInfo(): string {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  private getSessionInfo() {
    return {
      sessionId: this.generateSessionId(),
      pageLoadTime: performance.now().toString(),
      memoryUsage: (performance as any).memory ? 
        `${Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024)}MB` : 'Unknown',
    };
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private shouldCaptureGlobalEvent(message?: string): boolean {
    const msg = (message || '').toString();
    // Ignore benign browser errors (common noise on scroll/resize)
    const ignoredPatterns = [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ];
    return !ignoredPatterns.some((p) => msg.includes(p));
  }

  private buildErrorKey(errorData: Omit<ErrorData, 'id' | 'timestamp'>): string {
    const message = (errorData.message || '').slice(0, 200);
    const stack = (errorData.stack || '').slice(0, 200);
    const url = errorData.url || '';
    const type = errorData.type || '';
    return `${type}|${message}|${url}|${stack}`;
  }

  public captureError(errorData: Omit<ErrorData, 'id' | 'timestamp'>): void {
    // Dedupe identical errors in a short window to prevent loops/spam
    const key = this.buildErrorKey(errorData);
    const now = Date.now();
    const last = this.recentErrorTimestamps.get(key) || 0;
    if (now - last < this.dedupeWindowMs) {
      return;
    }
    this.recentErrorTimestamps.set(key, now);
    // Garbage collect old entries occasionally
    if (this.recentErrorTimestamps.size > 500) {
      const cutoff = now - this.dedupeWindowMs;
      const keysToDelete: string[] = [];
      this.recentErrorTimestamps.forEach((t, k) => {
        if (t < cutoff) keysToDelete.push(k);
      });
      keysToDelete.forEach((k) => this.recentErrorTimestamps.delete(k));
    }

    const fullErrorData: ErrorData = {
      ...errorData,
      id: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };

    // Add to queue
    this.errorQueue.push(fullErrorData);

    // Limit queue size
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }

    // Process queue asynchronously
    this.processQueue();
  }

  public captureApiError(
    endpoint: string,
    method: string,
    error: any,
    requestData?: any
  ): void {
    const severity = this.determineApiErrorSeverity(error);
    
    // Get current user ID
    const getCurrentUserId = () => {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          return user.id || user.user_id;
        }
        return null;
      } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
      }
    };

    // Get current user email
    const getCurrentUserEmail = () => {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          return user.email;
        }
        return null;
      } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
      }
    };
    
    this.captureError({
      type: 'API_ERROR',
      severity,
      message: error?.message || `API Error: ${method} ${endpoint}`,
      stack: error?.stack || '',
      url: window.location.href,
      userAgent: navigator.userAgent,
      userId: getCurrentUserId(),
      userEmail: getCurrentUserEmail(),
      deviceInfo: this.getDeviceInfo(),
      sessionInfo: this.getSessionInfo(),
      apiDetails: {
        endpoint,
        method,
        statusCode: error?.response?.status?.toString() || '',
        requestData: JSON.stringify(requestData || {}),
        responseData: JSON.stringify(error?.response?.data || {}),
      },
      resolved: false,
      resolvedBy: '',
      notes: '',
    });
  }

  private determineApiErrorSeverity(error: any): 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' {
    const statusCode = error?.response?.status;
    
    if (statusCode >= 500) return 'CRITICAL';
    if (statusCode >= 400) return 'HIGH';
    if (statusCode >= 300) return 'MEDIUM';
    return 'LOW';
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.errorQueue.length === 0) {
      return;
    }

    this.isProcessing = true;

    try {
      const errorData = this.errorQueue.shift();
      if (errorData) {
        // Send to backend API
        await this.sendErrorToBackend(errorData);
        
        // Also dispatch to Redux store
        store.dispatch(createError({
          ...errorData,
          platformType: 'WEB',
          appVersion: process.env.REACT_APP_VERSION || '1.0.0'
        }));
      }
    } catch (error) {
      // Silent fail for queue processing errors
    } finally {
      this.isProcessing = false;
      
      // Process next error if queue is not empty
      if (this.errorQueue.length > 0) {
        setTimeout(() => this.processQueue(), 100);
      }
    }
  }

  private async sendErrorToBackend(errorData: ErrorData): Promise<void> {
    try {
      const response = await CreateCrashlyticsError({
        ...errorData,
        platformType: 'WEB',
        appVersion: process.env.REACT_APP_VERSION || '1.0.0'
      });

      if (response.status !== 200) {
        // Silent fail for backend errors
      }
    } catch (backendError) {
      // Silent fail for backend errors
    }
  }

  public resolveError(errorId: string, resolvedBy: string, notes?: string): void {
    store.dispatch(updateError({
      id: errorId,
      data: {
        resolved: true,
        resolvedBy,
        resolvedAt: new Date(),
        notes,
      }
    }));
  }

  public getErrorStats() {
    const state = store.getState();
    const errors = (state as any).crashlytics?.errors || [];
    
    return {
      total: errors.length,
      resolved: errors.filter((e: ErrorData) => e.resolved).length,
      unresolved: errors.filter((e: ErrorData) => !e.resolved).length,
      byType: errors.reduce((acc: Record<string, number>, error: ErrorData) => {
        acc[error.type] = (acc[error.type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      bySeverity: errors.reduce((acc: Record<string, number>, error: ErrorData) => {
        acc[error.severity] = (acc[error.severity] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
    };
  }

  public clearOldErrors(daysToKeep: number = 30): void {
    // This functionality should be handled by the backend API
    // The frontend should not be responsible for data cleanup
  }
}

export default CrashlyticsService;
